<template>
  <SideMenuLayout title="Comptabilité" :items="items">
    <template #controls>
      <CustomForm ref="form" class="shared-form" :inputs=inputs :submit=buttons.submitCustom>
        <div v-if="isAccountantModule" class="professional">
          <CustomSelectField ref="professionalSelect" :content=inputs.professionals />
        </div>
        <div class="period">
          <template v-if="isPeriodSelected && !isEditingPeriod">
            <span class="period-title">Période</span>
            <span class="period-value">{{formattedStart}} &rarr; {{formattedEnd}}</span>
            <button type="button" class="period-edit" @click="isEditingPeriod = true">modifier</button>
          </template>
          <template v-else>
            <CustomTextField :content=inputs.defaultTvaStartPeriodDate class="period-field" />
            <CustomTextField :content=inputs.defaultTvaEndPeriodDate class="period-field" />
          </template>
        </div>
        <CustomButton style="display: none" class="period-submit" :params=buttons.submitCustom />
      </CustomForm>
    </template>
    <slot/>
  </SideMenuLayout>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import {formattedDate} from "@/utils";
import {AccountingService} from "@/accounting/services/AccountingService";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import {GroupController} from "@/controllers/user/GroupController";
import CustomForm from "@/components/forms/CustomForm.vue";
import SideMenuLayout from "@/components/SideMenuLayout.vue";

export default {
  name: "AccountingHeader",
  components: {
    SideMenuLayout,
    CustomForm,
    CustomSelectField,
    CustomTextField,
    CustomButton,
  },
  data() {
    return {
      isEditingPeriod: false,
      inputs: {
        defaultTvaStartPeriodDate: {label: 'Début de la période', value: '', type: 'date'},
        defaultTvaEndPeriodDate: {label: 'Fin de la période', value: '', type: 'date'},
        professionals: {label: 'Professionnel', value: '', type: 'select', selectValues: [], required: true},
      },
      buttons: {
        submitCustom: {text: "Valider", disabled: true}
      }
    };
  },
  computed: {
    items() {
      const items = [
        {to: '/accounting', label: 'Factures', icon: 'receipt'},
        {to: '/collections', label: 'Encaissements', icon: 'wallet'},
      ];
      if (this.hasPurchase) {
        items.push({to: '/accounting-purchases', label: 'Achats', icon: 'bag'});
        items.push({to: '/accounting-disbursements', label: 'Décaissements', icon: 'outgoing'});
      }
      items.push({to: '/accounting-export', label: 'Export comptable', icon: 'download'});
      return items;
    },
    isAccountantModule(){
      return localStorage && localStorage.options && localStorage.options.includes('accountant');
    },
    // Achats et décaissements : l'option achats du professionnel (un comptable consulte ceux de ses professionnels, qui l'ont ou non)
    hasPurchase(){
      return this.isAccountantModule || !!(localStorage && localStorage.options && localStorage.options.includes('purchase'));
    },
    formattedDate() {
      return formattedDate
    },
    isPeriodSelected() {
      return AccountingService.data.selectedPeriod.StartPeriodDate && AccountingService.data.selectedPeriod.EndPeriodDate;
    },
    formattedStart() {
      return AccountingService.data.selectedPeriod
          ? (new Date(AccountingService.data.selectedPeriod.StartPeriodDate)).toLocaleDateString("en-GB", this.formattedDate)
          : '';
    },
    formattedEnd() {
      return AccountingService.data.selectedPeriod
          ? (new Date(AccountingService.data.selectedPeriod.EndPeriodDate)).toLocaleDateString("en-GB", this.formattedDate)
          : '';
    },
  },
  watch: {
    "inputs.defaultTvaStartPeriodDate.value"() {
      this.submitIfComplete();
    },
    "inputs.defaultTvaEndPeriodDate.value"() {
      this.submitIfComplete();
    },
    "inputs.professionals.value"() {
      this.submitIfComplete();
    },
  },
  async mounted() {
    // Un comptable doit choisir un client : le formulaire n'est pas complet tant qu'aucun n'est sélectionné
    if (this.isAccountantModule && !AccountingService.data.selectedProfessional) AccountingService.data.isFormCompleted = false;
    if (this.isAccountantModule) {
      await this.fillProfessionalsSelect();
    } else {
      await AccountingService.init();
    }

    if (AccountingService.data.selectedPeriod) {
      this.inputs.defaultTvaStartPeriodDate.value = AccountingService.data.selectedPeriod.StartPeriodDate;
      this.inputs.defaultTvaEndPeriodDate.value = AccountingService.data.selectedPeriod.EndPeriodDate;
    } else {
      // Aucune période : le sélecteur doit être directement utilisable, pas de bouton intermédiaire.
      this.isEditingPeriod = true;
    }
  },
  methods: {
    submitIfComplete() {
      const startDate = this.inputs.defaultTvaStartPeriodDate.value;
      const endDate = this.inputs.defaultTvaEndPeriodDate.value;
      const professional = this.inputs.professionals.value;

      const isPeriodComplete = !!startDate && !!endDate;
      const isProfessionalComplete = !this.isAccountantModule || !!professional;

      if (!isPeriodComplete || !isProfessionalComplete) {
        AccountingService.data.isFormCompleted = false;
        return;
      }

      AccountingService.setPeriod(startDate, endDate);
      if (this.isAccountantModule) {
        AccountingService.setProfessional(professional);
      }

      this.isEditingPeriod = false;
      AccountingService.data.isFormCompleted = true;
    },
    async fillProfessionalsSelect(){
      // Get the table data
      this.$refs.form?.startLoading();
      this.inputs.professionals.selectValues = [];
      const resp = await GroupController.getForAccountant();
      if(!resp.errors){
        resp.sort((a, b) => a.name.localeCompare(b.name));
        for(let i=0; i < resp.length ;i++){
          this.inputs.professionals.selectValues.push({label:resp[i].name, value: resp[i]});

          // Fill the select with the current pro
          if (!!AccountingService.data.selectedProfessional?.name && resp[i].name === AccountingService.data.selectedProfessional.name){
            this.inputs.professionals.value = resp[i];
          }
        }
      }
      this.$refs.form?.stopLoading();
    },
  }
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl'

.shared-form
  display flex
  flex-direction column
  gap 8px

  .period
    display flex
    flex-direction column
    gap 4px

  .period-title
    font-size 12px
    font-weight 600
    color CTRL_TEXT_MUTED

  .period-value
    font-size 14px
    font-weight 700
    color FONT_COLOR

  .period-edit
    align-self flex-start
    border none
    background none
    padding 0
    color THEME_COLOR_PRIMARY
    font-size 13px
    font-weight 600
    cursor pointer
    text-decoration underline

    &:hover
      opacity .8
</style>
